// src/shared/bug-reporter/hooks/useReporterSubmit.ts
// Multipart POST to the team-report ingest endpoint. The widget owns its own
// fetch path so it does not depend on the host app's axios instance — keeping
// the canonical decoupled from per-app conventions.

import { useCallback, useState } from 'react'
import type {
  AutoCapturePayload,
  ReporterFormValues,
  ReporterSubmitResult,
} from '../types'

interface SubmitParams {
  apiBaseUrl: string
  getAuthToken?: () => string | null
}

export class BugReporterSubmitError extends Error {
  constructor(message: string, public status?: number) {
    super(message)
    this.name = 'BugReporterSubmitError'
  }
}

const DEFAULT_TOKEN_KEY = 'palate_ops_token'

function defaultGetAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(DEFAULT_TOKEN_KEY)
}

export function useReporterSubmit({ apiBaseUrl, getAuthToken }: SubmitParams) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<BugReporterSubmitError | null>(null)
  const [lastResult, setLastResult] = useState<ReporterSubmitResult | null>(null)

  const submit = useCallback(
    async (form: ReporterFormValues, autoCapture: AutoCapturePayload): Promise<ReporterSubmitResult> => {
      setIsSubmitting(true)
      setError(null)

      const body = new FormData()
      body.set('title', form.title)
      body.set('description', form.description)
      if (form.severity) body.set('severity', form.severity)
      if (form.category) body.set('category', form.category)
      body.set('autoCapture', JSON.stringify(autoCapture))
      for (const file of form.attachments) {
        body.append('attachments', file)
      }

      const token = (getAuthToken ?? defaultGetAuthToken)()
      const url = `${apiBaseUrl.replace(/\/$/, '')}/internal/quality/ingest/team`

      try {
        const headers: Record<string, string> = {}
        if (token) headers.Authorization = `Bearer ${token}`

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body,
        })

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          const message =
            response.status === 401 || response.status === 403
              ? 'You are not authorized to submit a bug report. Try signing in again.'
              : response.status >= 500
              ? 'The Quality service is unavailable. Please try again in a moment.'
              : text || `Request failed with ${response.status}`
          const err = new BugReporterSubmitError(message, response.status)
          setError(err)
          throw err
        }

        // Backend wraps responses with { statusCode, data, ... } — unwrap if needed.
        const payload = await response.json().catch(() => ({}))
        const data = (payload && typeof payload === 'object' && 'data' in payload)
          ? (payload as { data: ReporterSubmitResult }).data
          : (payload as ReporterSubmitResult)

        setLastResult(data)
        return data
      } catch (err) {
        if (err instanceof BugReporterSubmitError) throw err
        const message = err instanceof Error ? err.message : 'Network error'
        const wrapped = new BugReporterSubmitError(message)
        setError(wrapped)
        throw wrapped
      } finally {
        setIsSubmitting(false)
      }
    },
    [apiBaseUrl, getAuthToken],
  )

  const reset = useCallback(() => {
    setError(null)
    setLastResult(null)
  }, [])

  return { submit, isSubmitting, error, lastResult, reset }
}
